import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Drawer from '../../../../components/drawer/drawer';
import Form, { SubmitType } from '../../../../components/form';
import useCreateFooter from './use-create-footer';
import { usedProductSchema, useUsedProductForm } from './use-used-product-form';
import { useWarehouseContext } from './warehouse-context';
import { UseFormReturn } from 'react-hook-form';
import { of } from 'rxjs';
import DealProduct from '../../../../interfaces/deal-product';
import ClassifierValue from '../../../../interfaces/classifierValue';
import { Product } from '../../../../interfaces/product';
import { ProductObtainType } from '../../../../interfaces/product-obtain-type';

const DrawerFormUsedProducts = ({
  formMethods,
  drawerModalFormMethods,
}: {
  formMethods: UseFormReturn;
  drawerModalFormMethods: UseFormReturn;
}) => {
  const {
    isOpenUsedProducts,
    setIsOpenUsedProducts,
    formValues,
    setFormValues,
    setIsPartnerRequired,
    setIsTargetUsageRequired,
  } = useWarehouseContext();

  const PLANT_PROTECTION_PRODUCTS = 'system value - plant protection products';
  const BIOCIDES = 'system value - biocides';

  const dealStateTemp: DealProduct = useMemo(
    () => ({
      packaging_size: '',
      packaging_type_id: { value: '', label: '' },
      packaging_unit: { label: '', value: '' },
      packaging_amount: '',
      packaging_price: '',
      partner_id: { value: '', label: '' },
      is_partner_required: false,
      type: { label: '', value: '' },
      product_category_id: { label: '', value: '', type: 'any' },
      vat_sum: '',
      product_obtain_type_id: '',
      product_type: {
        id: '',
        classifer_id: '',
        parent_id: '',
        parent: '',
        value: '',
      } as unknown as ClassifierValue,
      product_type_id: { label: '', value: '', veterinary: false },
      product_id: { label: '', value: '', obtain_type_id: '' },
      amount: null,
      unit: { label: '', value: '' },
      warehouse_id: { label: '', value: '' },
      to_warehouse_id: { label: '', value: '' },
      price: '',
      sum: '',
      vat_rate: {
        label: '',
        value: '',
        rate: '',
      } as unknown as ClassifierValue,
      vat: '',
      sum_with_vat: '',
      id: '',
      product_obtain_type: { name: '' } as unknown as ProductObtainType,
      'product_obtain_type.name': '',
      'product_category.value': '',
      product_category: {
        value: '',
        name: '',
      } as unknown as ClassifierValue,
      'warehouse.name': '',
      'to_warehouse_id.name': '',
      'warehouse_id.label': '',
      'to_warehouse_id.label': '',
      'product_type.value': '',
      'product_type_id.label': '',
      'product.name': '',
      'product_id.label': '',
      product: { name: '' } as unknown as Product,
      packaging_type: {
        id: '',
        classifer_id: '',
        parent_id: '',
        parent: '',
        value: '',
      } as unknown as ClassifierValue,
      'warehouse_id.value': '',
      'to_warehouse_id.value': '',
      price_amount: '',
      vat_rate_id: '',
      vat_amount: '',
      reason: '',
      'vat_rate.label': '',
      product_warehouses: [],
    }),
    []
  );

  const [dealState, setDealState] = useState<DealProduct>(dealStateTemp);

  const handleCloseDrawer = useCallback(() => {
    setDealState(dealStateTemp);
    setFormValues(dealStateTemp);
    setIsOpenUsedProducts(false);
    drawerModalFormMethods.reset();
    drawerModalFormMethods.setValue('product_id', '');
    drawerModalFormMethods.setValue('product_type_id', '');
    drawerModalFormMethods.setValue('product_category_id', '');
  }, [
    setDealState,
    setFormValues,
    setIsOpenUsedProducts,
    dealStateTemp,
    drawerModalFormMethods,
  ]);

  const submit = useCallback<SubmitType<DealProduct>>(
    (form) => {
      let currentLostProducts = formMethods.getValues('lost_products');

      if (!form?.id || currentLostProducts.length === 0) {
        form.id = form.id
          ? form.id
          : `${form?.warehouse_id?.value}_${
              form?.product_id?.value
            }_${Date.now()}`;
        currentLostProducts.push(form);
      } else {
        currentLostProducts = currentLostProducts.map((product) =>
          product.id === form.id ? form : product
        );
      }

      if (
        form?.product_category_id['type'] &&
        form.product_category_id['type'] === PLANT_PROTECTION_PRODUCTS
      ) {
        setIsPartnerRequired(true);
      }

      if (
        form?.product_category_id['type'] &&
        form.product_category_id['type'] === BIOCIDES
      ) {
        setIsTargetUsageRequired(true);
      }

      formMethods.setValue('lost_products', currentLostProducts);

      handleCloseDrawer();

      return [of(null)];
    },
    [
      formMethods,
      handleCloseDrawer,
      setIsTargetUsageRequired,
      setIsPartnerRequired,
    ]
  );

  useEffect(() => {
    if (isOpenUsedProducts === true) {
      setDealState(!formValues?.id ? { ...dealStateTemp } : { ...formValues });
    }
  }, [isOpenUsedProducts, formValues, dealStateTemp, setDealState]);

  const body = useUsedProductForm(formValues);
  const createFooter = useCreateFooter(handleCloseDrawer);

  return (
    <Drawer isOpen={isOpenUsedProducts}>
      <Form
        title={'pages.warehouse.deals.add_used_product'}
        mode="step"
        body={body}
        footer={createFooter}
        values={dealState}
        schema={usedProductSchema()}
        submit={submit}
        formMethods={drawerModalFormMethods}
      />
    </Drawer>
  );
};

export default DrawerFormUsedProducts;
